import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  listCategories,
  createCategory,
  createSubcategory,
  renameCategory,
  deleteCategory,
  setCategoryLimit,
} from '../services/categoryService'
import type { Category } from '../types'

export function useCategories(householdId: string | null) {
  const [all, setAll]         = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!householdId) return
    setLoading(true)
    setError(null)
    try {
      setAll(await listCategories(householdId))
    } catch (err) {
      console.error('[useCategories] fetch error:', err)
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [householdId])

  useEffect(() => { fetch() }, [fetch])

  const categories    = useMemo(() => all.filter(c => !c.parent_id), [all])
  const subcategories = useMemo(() => all.filter(c => !!c.parent_id), [all])

  async function addCategory(name: string, kind: 'in' | 'out', spending_limit?: number | null): Promise<Category> {
    if (!householdId) throw new Error('Sem household')
    const cat = await createCategory(householdId, name, kind, spending_limit)
    setAll(prev => [...prev, cat].sort((a, b) => a.name.localeCompare(b.name)))
    return cat
  }

  async function addSubcategory(name: string, parentId: string): Promise<void> {
    if (!householdId) return
    const sub = await createSubcategory(householdId, name, parentId)
    setAll(prev => [...prev, sub].sort((a, b) => a.name.localeCompare(b.name)))
  }

  async function editCategory(id: string, name: string): Promise<void> {
    await renameCategory(id, name)
    setAll(prev => prev.map(c => c.id === id ? { ...c, name } : c))
  }

  async function setLimit(id: string, limit: number | null): Promise<void> {
    await setCategoryLimit(id, limit)
    setAll(prev => prev.map(c => c.id === id ? { ...c, spending_limit: limit } : c))
  }

  async function removeCategory(id: string): Promise<{ error?: string }> {
    try {
      await deleteCategory(id)
      setAll(prev => prev.filter(c => c.id !== id && c.parent_id !== id))
      return {}
    } catch (err) {
      console.error('[useCategories] removeCategory error:', err)
      return { error: (err as Error).message }
    }
  }

  return { categories, subcategories, loading, error, addCategory, addSubcategory, editCategory, setLimit, removeCategory }
}
